<template>
  <div class="flex w-full flex-col gap-4 pt-3">
    <div v-if="subscriptionState.isEligible" class="flex w-full flex-col gap-4">
      <template v-if="slots['tax-extras']">
        <slot name="tax-extras"></slot>
      </template>
      <div class="rounded-md bg-shade-300 p-4 text-sm">
        <p class="flex gap-1">
          <LazyIconFielmannCheck class="mb-auto size-4" />
          {{ $i18n.t('subscription.benefit.free_shipping') }}
        </p>
        <p
          v-if="subscriptionDiscounts && subscriptionDiscounts.subsequent"
          class="flex gap-1"
        >
          <LazyIconFielmannCheck class="mb-auto size-4" />
          {{
            $i18n.t(
              hasInitialDiscount
                ? 'subscription.benefit.save_all'
                : 'subscription.benefit.save',
              {
                discount:
                  subscriptionDiscounts.subsequent.value +
                  subscriptionDiscounts.subsequent.unit,
              },
            )
          }}
        </p>
        <p class="flex gap-1">
          <LazyIconFielmannCheck class="mb-auto size-4" />
          {{ $i18n.t('subscription.benefit.cancellable') }}
        </p>
      </div>
      <ClientOnly>
        <div class="flex w-full flex-col gap-4">
          <div>
            <div class="mb-1 text-xs font-medium text-gray-600">
              {{ $t('subscription.interval') }}
            </div>
            <Dropdown
              v-model="selectedInterval"
              class="w-full"
              :items="subscriptionIntervals"
              @update:model-value="onChange"
            >
              <template #default>
                <div class="flex items-center gap-2">
                  <LazyIconFielmannReturn class="size-5" />
                  {{ selectedInterval?.label }}
                </div>
              </template>
              <template #item="{ item, selectItem }">
                <div
                  :key="`${item}`"
                  class="block cursor-pointer px-4 py-2 text-sm hover:bg-gray-200 active:bg-gray-300"
                  @click="selectItem(item)"
                >
                  {{ item.label }}
                </div>
              </template>
            </Dropdown>
          </div>
          <div>
            <div class="mb-1 text-xs font-medium text-gray-600">
              {{ $t('subscription.follow_up_delivery') }}
            </div>
            <Dropdown
              v-model="selectedPreferredDeliveryDate"
              class="w-full"
              :items="preferredDeliveryDate"
              @update:model-value="onChange"
            >
              <template #default>
                <div class="flex items-center gap-2">
                  <LazyIconFielmannCalendar
                    class="size-4"
                    style="stroke-width: 2"
                  />
                  {{
                    $t('subscription.' + selectedPreferredDeliveryDate?.label, {
                      dayOfMonth:
                        selectedPreferredDeliveryDate?.day +
                        $t(`global.ordinal_suffixes.${ordinalSuffixKey}`),
                    })
                  }}
                </div>
              </template>
              <template #item="{ item, selectItem }">
                <div
                  :key="`${item}`"
                  class="block cursor-pointer px-4 py-2 text-sm hover:bg-gray-200 active:bg-gray-300"
                  @click="selectItem(item)"
                >
                  {{
                    $t('subscription.day_of_month_selection_caption', {
                      dayOfMonth:
                        item?.day +
                        $t(`global.ordinal_suffixes.${ordinalSuffixKey}`),
                    })
                  }}
                </div>
              </template>
            </Dropdown>
          </div>
        </div>
      </ClientOnly>
    </div>
    <div
      v-else-if="!subscriptionState.isEligible"
      class="px-8 py-4 text-center text-primary"
    >
      {{ $t('subscription.not_eligible_message') }}
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, toRefs, watch } from 'vue'
import type { Product, Variant } from '@scayle/storefront-nuxt'
import { useSubscription } from '../composables/useSubscription'
import type { PreferredDeliveryDate } from '../helpers/subscription'

type Props = {
  product: Product
  variants?: Array<Variant | undefined>
  quantity?: number
  preferredDeliveryDate?: Array<PreferredDeliveryDate>
  pricePromotionKey?: string
  values?: SubscriptionDefinition
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variants: undefined,
  quantity: 1,
  values: undefined,
  disabled: false,
  pricePromotionKey: 'subscription',
  preferredDeliveryDate: () => getSubscriptionDeliveryDates(),
})

const emit = defineEmits(['change'])

const slots = useSlots()

const { $i18n } = useNuxtApp()
const scope = effectScope()
onScopeDispose(() => scope.stop())

const { product, variants, pricePromotionKey, values } = toRefs(props)

const {
  subscriptionIntervals,
  selectedInterval,
  selectedPreferredDeliveryDate,
  subscriptionVariantsEligible,
  subscriptionTerm,
  subscriptionCancellationPolicy,
  ordinalSuffixKey,
  subscriptionPrice,
  subsequentPrice,
  subscriptionDiscounts,
} = await useSubscription(
  product,
  pricePromotionKey,
  variants,
  `product-subscription-${props.product.id}`,
)

const hasInitialDiscount = computed(
  () => subscriptionPrice.value?.withTax === subsequentPrice.value,
)

const onChange = () => {
  const data: SubscriptionChangeData = {
    pricePromotionKey: toValue(pricePromotionKey),
    subscriptionTerm: toValue(subscriptionTerm),
    subscriptionCancellationPolicy: toValue(subscriptionCancellationPolicy),
    selectedInterval: toValue(selectedInterval),
    selectedPreferredDeliveryDate: toValue(selectedPreferredDeliveryDate),
  }
  emit('change', data)
}

const subscriptionState = computed(() => ({
  isInitial: !props.variants,
  isEligible: !!props.variants && subscriptionVariantsEligible.value,
}))

selectedPreferredDeliveryDate.value = getPreferredDeliveryDate(
  props.preferredDeliveryDate,
)

scope.run(() => {
  if (import.meta.server) {
    return
  }

  // Set initial dropdown values if values are passed as props
  watch(
    () => values,
    () => {
      if (!values.value) {
        return
      }

      const subscriptionInterval = values.value?.subscriptionInterval
      if (subscriptionInterval) {
        const newValue = subscriptionIntervals.value.find(
          (interval) => interval.value === subscriptionInterval,
        )

        if (newValue) {
          selectedInterval.value = newValue
        }
      }

      const subscriptionDeliveryDate = values.value?.subscriptionDeliveryDate
      if (subscriptionDeliveryDate) {
        const newValue =
          props.preferredDeliveryDate.find(
            (preferredDeliveryDate) =>
              preferredDeliveryDate.day === toValue(subscriptionDeliveryDate),
          ) || props.preferredDeliveryDate[0]

        if (newValue) {
          selectedPreferredDeliveryDate.value = newValue
        }
      }
    },
    { immediate: true },
  )

  // set initial values if subscriptionIntervals change
  watch(
    () => subscriptionIntervals.value,
    () => {
      if (!subscriptionIntervals.value.length) {
        selectedInterval.value = undefined
        return
      }

      const selectedIntervalIsInvalid = !subscriptionIntervals.value.some(
        (interval) => interval.value === selectedInterval.value,
      )

      if (!selectedIntervalIsInvalid) {
        return
      }

      const initialInterval = values.value?.subscriptionInterval
      if (initialInterval) {
        selectedInterval.value =
          subscriptionIntervals.value.find(
            (interval) => interval.value === initialInterval,
          ) || subscriptionIntervals.value[0]
      } else {
        selectedInterval.value = subscriptionIntervals.value[0]
      }
    },
    { immediate: true },
  )
})
</script>
